// ABOUTME: Actions step of the bulk change: one card per action (set, clear, copy, replace text, links, translate, delete) and "Add action".
// ABOUTME: Blocks "Compute preview" while an action is incomplete or two actions change the same field and language.

import { AddIcon, Alert, Box, Button } from '@ds/coolshop';
import { ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { BulkChangeAction, FindReplaceOptions, isDeleteRun } from '../types';
import { defaultAction, findDuplicateSlots, isActionComplete } from '../utils/actionDrafts';
import { ContentTypeLabeler } from '../utils/contentTypeLabels';
import { ActionCard } from './ActionCard';
import { StepFrame } from './StepFrame';

/** The preview can be computed: at least one action, all complete, no two on the same slot. */
export const canComputePreview = (actions: BulkChangeAction[]) =>
    actions.length > 0 && actions.every(isActionComplete) && findDuplicateSlots(actions).size === 0;

type ActionsStepProps = {
    actions: BulkChangeAction[];
    onChange: (actions: BulkChangeAction[]) => void;
    options: FindReplaceOptions | null;
    labeler: ContentTypeLabeler;
    sidebar?: ReactNode;
    /** Without them the step renders no footer (the rule edit page). */
    onBack?: () => void;
    onNext?: () => void;
    computing?: boolean;
    computeError?: string | null;
};

export function ActionsStep({
    actions,
    onChange,
    options,
    labeler,
    sidebar,
    onBack,
    onNext,
    computing = false,
    computeError = null,
}: ActionsStepProps) {
    const { formatMessage } = useIntl();
    const duplicates = useMemo(() => findDuplicateSlots(actions), [actions]);
    // A delete is the only action of its run.
    const deletes = isDeleteRun({ actions });

    const footer =
        onBack || onNext ? (
            <>
                {onBack && (
                    <Button variant="text" onClick={onBack} disabled={computing}>
                        {formatMessage({ id: 'bulkChange.back', defaultMessage: 'Back' })}
                    </Button>
                )}
                {onNext && (
                    <Button
                        variant="contained"
                        color="secondary"
                        data-testid="find-replace-scan-button"
                        onClick={onNext}
                        disabled={!canComputePreview(actions) || computing}
                        loading={computing}
                    >
                        {formatMessage({
                            id: 'bulkChange.actions.computePreview',
                            defaultMessage: 'Compute preview',
                        })}
                    </Button>
                )}
            </>
        ) : undefined;

    return (
        <StepFrame
            title={formatMessage({
                id: 'bulkChange.actions.title',
                defaultMessage: 'What should change?',
            })}
            subtitle={
                deletes
                    ? formatMessage({
                          id: 'bulkChange.actions.subtitleDelete',
                          defaultMessage:
                              'Every object in scope is deleted. Nothing is deleted until you review the preview and apply it.',
                      })
                    : formatMessage({
                          id: 'bulkChange.actions.subtitle',
                          defaultMessage:
                              'Each action changes one field on every object in scope. Nothing is written until you review the preview and apply it.',
                      })
            }
            sidebar={sidebar}
            footer={footer}
        >
            {actions.map((action, index) => (
                <ActionCard
                    // Actions have no identity of their own: their position is it.
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    index={index}
                    action={action}
                    onChange={next =>
                        onChange(
                            next.kind === 'deleteNode'
                                ? [next]
                                : actions.map((current, i) => (i === index ? next : current)),
                        )
                    }
                    onRemove={() => onChange(actions.filter((_action, i) => i !== index))}
                    contentTypes={options?.contentTypes ?? []}
                    locales={options?.locales ?? []}
                    translatableLocales={options?.translatableLocales ?? []}
                    deleteDisabled={options?.deleteDisabled ?? false}
                    labeler={labeler}
                    duplicate={duplicates.has(index)}
                />
            ))}
            {!deletes && (
                <Box>
                    <Button
                        variant="text"
                        startIcon={<AddIcon />}
                        data-testid="bulk-change-add-action"
                        onClick={() => onChange([...actions, defaultAction('setValue')])}
                    >
                        {formatMessage({
                            id: 'bulkChange.actions.add',
                            defaultMessage: 'Add action',
                        })}
                    </Button>
                </Box>
            )}
            {computeError && <Alert severity="error">{computeError}</Alert>}
        </StepFrame>
    );
}
